"use client";

import React from "react";
import Link from "next/link";
import { Phone, Mail, ExternalLink, Cookie } from "lucide-react";
import { CONTACT_INFO, APP_CONFIG, LEGAL_INFO } from "@/utils/constants/app";
import { FOOTER_CONSTANTS } from "@/utils/constants/components/footer";
import { PhoneLink, EmailLink, createWhatsAppLink } from "@/utils/internal/linkHelpers";
import { handleAnchorClick } from "@/utils/internal/smoothScroll";
import { useCookieConsent } from "@/contexts/cookie-consent-context";

export function Footer() {
  const { setShowSettings } = useCookieConsent();
  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8 justify-center max-w-5xl mx-auto">
          {/* Company Info */}
          <div className="space-y-4">
            <div>
              <h3 className="text-2xl font-bold text-green-400 mb-2">
                {APP_CONFIG.COMPANY_NAME}
              </h3>
              <p className="text-gray-400 leading-relaxed">
                {FOOTER_CONSTANTS.COMPANY_DESCRIPTION}
              </p>
            </div>
          </div>

          {/* Home Sections */}
          <div>
            <h4 className="text-lg font-semibold mb-4">
              {FOOTER_CONSTANTS.HOME_SECTIONS.TITLE}
            </h4>
            <ul className="space-y-2">
              {FOOTER_CONSTANTS.HOME_SECTIONS.SECTIONS.map((section) => (
                <li key={section.sectionId}>
                  <Link
                    href={`/#${section.sectionId}`}
                    onClick={(e) => handleAnchorClick(e, `/#${section.sectionId}`)}
                    className="text-gray-400 hover:text-green-400 text-sm transition-colors text-left group"
                  >
                    <div>
                      <div className="font-medium">{section.name}</div>
                      <div className="text-xs text-gray-500 group-hover:text-green-300 transition-colors">
                        {section.description}
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-4">
              {FOOTER_CONSTANTS.SERVICES.TITLE}
            </h4>
            <ul className="space-y-2">
              {FOOTER_CONSTANTS.SERVICES.LIST.map((service) => {
                let href = "/";
                switch (service.page) {
                  case "disinfestazione":
                    href = "/disinfestazione";
                    break;
                  case "derattizzazione":
                    href = "/derattizzazione";
                    break;
                  case "pest-management":
                    href = "/altri-servizi/pest-management";
                    break;
                  case "sanificazione":
                    href = "/altri-servizi/sanificazione";
                    break;
                  case "diserbo":
                    href = "/altri-servizi/diserbo";
                    break;
                  case "allontanamento-volatili":
                    href = "/altri-servizi/allontanamento-volatili";
                    break;
                }
                return (
                  <li key={service.page}>
                    <Link href={href} className="text-gray-400 hover:text-green-400 text-sm transition-colors text-left">
                      {service.name}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-4">
              {FOOTER_CONSTANTS.CONTACT.TITLE}
            </h4>
            <div className="space-y-3">
              <div className="flex items-center">
                <Phone className="w-4 h-4 mr-3 text-green-400 flex-shrink-0" />
                <PhoneLink
                  phoneNumber={CONTACT_INFO.MAIN_PHONE}
                  className="text-gray-400 hover:text-green-400 text-sm transition-colors"
                />
              </div>
              <div className="flex items-center">
                <Mail className="w-4 h-4 mr-3 text-green-400 flex-shrink-0" />
                <EmailLink
                  email={CONTACT_INFO.PRIMARY_EMAIL}
                  className="text-gray-400 hover:text-green-400 text-sm transition-colors break-all"
                />
              </div>
              <div className="flex items-center">
                <Phone className="w-4 h-4 mr-3 text-green-400 flex-shrink-0" />
                <PhoneLink
                  phoneNumber={CONTACT_INFO.WHATSAPP_PHONE}
                  className="text-gray-400 hover:text-green-400 text-sm transition-colors"
                />
                <a
                  href={createWhatsAppLink(CONTACT_INFO.WHATSAPP_PHONE, CONTACT_INFO.WHATSAPP_DEFAULT_MESSAGE)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-3 text-xs text-green-300 hover:text-green-200"
                >
                  
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              <p>
                © {new Date().getFullYear()}{" "}
                {APP_CONFIG.COMPANY_NAME}.{" "}
                {FOOTER_CONSTANTS.COPYRIGHT}
              </p>
              <p className="mt-1">
                P.IVA: {LEGAL_INFO.VAT_NUMBER}
              </p>
            </div>

            {/* Legal Links */}
            <div className="flex flex-col md:flex-row md:justify-end space-y-2 md:space-y-0 md:space-x-6">
              <Link href="/privacy-policy" className="text-gray-400 hover:text-green-400 text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link href="/termini-servizio" className="text-gray-400 hover:text-green-400 text-sm transition-colors">
                Termini di Servizio
              </Link>
              <Link href="/cookie-policy" className="text-gray-400 hover:text-green-400 text-sm transition-colors">
                Cookie Policy
              </Link>
              <button
                onClick={() => setShowSettings(true)}
                className="text-gray-400 hover:text-green-400 text-sm transition-colors flex items-center gap-1 md:justify-end"
              >
                <Cookie className="w-4 h-4" />
                Gestisci Cookie
              </button>
            </div>
          </div>
        </div>

        {/* Craftale Credit Section - Enhanced Visibility */}
        <div className="border-t border-gray-800 mt-6 pt-6">
          <div className="text-center">
            <p className="text-gray-400 text-sm">
              Sito web realizzato da{" "}
              <a
                href="https://www.craftale.it"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-green-400 transition-colors font-semibold inline-flex items-center gap-1"
              >
                Craftale
                <ExternalLink className="w-3 h-3" />
              </a>
            </p>
          </div>
        </div>

        <div className="mt-4 text-center">
          <Link
            href="/admin-login"
            className="text-gray-400 hover:text-green-400 text-sm transition-colors"
            title="Accesso Amministratore"
          >
            ⚙️
          </Link>
        </div>
      </div>
    </footer>
  );
}